import { humanizeText, numberToCurrency } from '@/utils'
import type {
  Report,
  ReportChartSpec,
  ReportSection,
  ReportTableColumn,
  ReportTableSpec,
} from '../../../stores/types'
import { sankeyLabelsByName } from '../chartData'
import { formatCell, formatIsoDate, isNumeric } from '../format'
import { sectionAnchorId, stripDuplicateTitleHeading } from '../headings'
import { splitMarkdownTokens } from '../tokens'

const RECOMMENDATIONS_SECTION_ID = 'conclusion_and_recommendations'
const ACCOUNT_DETAIL_LIMIT = 4

export interface StatItem {
  label: string
  value: string
}

export interface TableBlock {
  heading?: string
  columns: { label: string; numeric: boolean }[]
  rows: string[][]
  totalRow?: string[]
  // "…and 12 more totaling $3,400.00." when rows were capped for display.
  note?: string
}

export type ReportBlock =
  | { kind: 'title'; title: string; createdAt?: string; caseType?: string }
  | { kind: 'stats'; stats: StatItem[] }
  | { kind: 'heading'; text: string; eyebrow?: string }
  | { kind: 'prose'; markdown: string }
  | { kind: 'table'; table: TableBlock }
  | { kind: 'chart'; id: string; title?: string; fallback?: TableBlock }

export interface ReportDocument {
  title: string
  blocks: ReportBlock[]
}

// Walks a report into a flat, renderer-agnostic block list in the same
// reading order as the on-screen document: masthead, recommendations,
// executive summary, then the numbered analysis sections. Both the PDF and
// the Word renderer consume this list, so the two exports never drift.
export function buildReportDocument(report: Report): ReportDocument {
  const title = report.title?.trim() || 'Forensic Accounting Report'
  const sectionTitles = new Map(
    report.sections.map((section) => [sectionAnchorId(section), section.title]),
  )
  const blocks: ReportBlock[] = []

  blocks.push({
    kind: 'title',
    title,
    createdAt: report.generated_at || undefined,
    caseType: report.case_type ? humanizeText(report.case_type) : undefined,
  })

  const stats = mastheadStats(report)
  if (stats.length > 0) blocks.push({ kind: 'stats', stats })

  const recommendations = report.sections.find(isRecommendationsSection)
  const bodySections = report.sections.filter(
    (section) => !isRecommendationsSection(section),
  )

  if (recommendations) {
    pushSection(blocks, recommendations, sectionTitles, {
      eyebrow: 'Recommended next steps',
    })
  }

  if (report.executive_summary?.trim()) {
    blocks.push({ kind: 'heading', text: 'Executive Summary' })
    blocks.push({
      kind: 'prose',
      markdown: stripDuplicateTitleHeading(
        report.executive_summary,
        'Executive Summary',
      ),
    })
  }

  bodySections.forEach((section, index) => {
    pushSection(blocks, section, sectionTitles, {
      eyebrow: `Section ${index + 1}`,
    })
  })

  return { title, blocks }
}

function pushSection(
  blocks: ReportBlock[],
  section: ReportSection,
  sectionTitles: Map<string, string>,
  { eyebrow }: { eyebrow: string },
): void {
  blocks.push({ kind: 'heading', text: section.title, eyebrow })

  const tableById = new Map(
    (section.tables ?? []).map((table) => [table.id ?? '', table]),
  )
  const chartById = new Map(
    (section.charts ?? []).map((chart) => [chart.id ?? '', chart]),
  )
  const placed = new Set<string>()

  const segments = splitMarkdownTokens(
    stripDuplicateTitleHeading(section.markdown ?? '', section.title),
  )
  for (const segment of segments) {
    if (segment.kind === 'text') {
      if (segment.text.trim())
        blocks.push({ kind: 'prose', markdown: segment.text })
      continue
    }
    placed.add(segment.id)
    if (segment.kind === 'table') {
      const table = tableById.get(segment.id)
      if (table)
        blocks.push({
          kind: 'table',
          table: buildTableBlock(table, sectionTitles),
        })
    } else {
      const chart = chartById.get(segment.id)
      if (chart) blocks.push(buildChartBlock(chart))
    }
  }

  for (const table of section.tables ?? []) {
    if (placed.has(table.id ?? '')) continue
    blocks.push({ kind: 'table', table: buildTableBlock(table, sectionTitles) })
  }
  for (const chart of section.charts ?? []) {
    if (placed.has(chart.id ?? '')) continue
    blocks.push(buildChartBlock(chart))
  }
}

function buildTableBlock(
  spec: ReportTableSpec,
  sectionTitles: Map<string, string>,
): TableBlock {
  const columns = spec.columns.map((column) => ({
    label: column.label || column.key,
    numeric: isNumeric(column.format),
  }))
  const cellFor = (row: Record<string, unknown>, column: ReportTableColumn) =>
    column.format === 'section_ref'
      ? resolveSectionRefs(row[column.key], sectionTitles)
      : formatCell(row[column.key], column.format)
  const rows = spec.rows.map((row) =>
    spec.columns.map((column) => cellFor(row, column)),
  )
  const totalRow = spec.total_row
    ? spec.columns.map((column) =>
        formatCell(spec.total_row?.[column.key], column.format),
      )
    : undefined

  // Citations render as a per-row Source column, the same as on screen,
  // instead of one long footer line that repeats the file name per page.
  const sources = rowSources(spec)
  if (sources) {
    columns.push({ label: 'Source', numeric: false })
    spec.rows.forEach((row, index) => {
      rows[index].push(sources.get(String(row.id ?? '')) ?? '')
    })
    totalRow?.push('')
  }

  return {
    heading: tableHeading(spec),
    columns,
    rows,
    totalRow,
    note: omittedNote(spec),
  }
}

function tableHeading(spec: ReportTableSpec): string | undefined {
  const label =
    spec.exhibit_number != null ? `Table ${spec.exhibit_number}` : undefined
  const heading = [label, spec.title?.trim()].filter(Boolean).join(' — ')
  return heading || undefined
}

function omittedNote(spec: ReportTableSpec): string | undefined {
  const count = spec.omitted_row_count ?? 0
  if (count <= 0) return undefined
  const suffix =
    spec.omitted_total != null
      ? ` totaling ${numberToCurrency(spec.omitted_total)}`
      : ''
  return `…and ${count.toLocaleString()} more${suffix}.`
}

// Maps each transaction id to its citation label ("statement.pdf · p.4"),
// so a row can show its own source. Mirrors the on-screen Source column.
function rowSources(spec: ReportTableSpec): Map<string, string> | null {
  const citations = spec.citations
  if (!citations?.length) return null
  const sources = new Map<string, string>()
  for (const citation of citations) {
    if (!citation.transaction_id || !citation.statement_file_name) continue
    const page = citation.source_page ? ` · p.${citation.source_page}` : ''
    sources.set(
      String(citation.transaction_id),
      `${citation.statement_file_name}${page}`,
    )
  }
  return sources.size > 0 ? sources : null
}

// A `section_ref` cell holds section id(s); show the section titles the way
// the on-screen evidence links do, falling back to a humanized id.
function resolveSectionRefs(
  value: unknown,
  sectionTitles: Map<string, string>,
): string {
  return sectionIdList(value)
    .map((id) => sectionTitles.get(id) ?? humanizeText(id))
    .join(', ')
}

function sectionIdList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean)
  }
  if (typeof value !== 'string') return []
  const trimmed = value.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed.map((entry) => String(entry).trim()).filter(Boolean)
      }
    } catch {
      // fall through to comma splitting
    }
  }
  return trimmed
    ? trimmed
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
    : []
}

// A chart carries its own data table so a chart that never rendered (an
// unsupported type, or one scrolled out of a lazy view) still exports its
// numbers instead of leaving a hole.
function buildChartBlock(spec: ReportChartSpec): ReportBlock {
  return {
    kind: 'chart',
    id: spec.id ?? '',
    title: spec.title ?? undefined,
    fallback: chartFallback(spec),
  }
}

function chartFallback(spec: ReportChartSpec): TableBlock | undefined {
  const valueFormat = spec.value_format ?? 'currency'
  if ((spec.type === 'bar' || spec.type === 'line') && spec.data?.length) {
    return {
      columns: [
        { label: 'Label', numeric: false },
        { label: spec.title?.trim() || 'Value', numeric: true },
      ],
      rows: spec.data.map((point) => [
        point.date,
        formatCell(point.value, valueFormat),
      ]),
    }
  }
  if (spec.type === 'sankey' && spec.links?.length) {
    const labels = sankeyLabelsByName(spec.nodes ?? [])
    const labelOf = (name: string) => labels.get(name) ?? name
    return {
      columns: [
        { label: 'From', numeric: false },
        { label: 'To', numeric: false },
        { label: 'Amount', numeric: true },
      ],
      rows: spec.links.map((link) => [
        labelOf(link.source),
        labelOf(link.target),
        formatCell(link.value, valueFormat),
      ]),
    }
  }
  return undefined
}

function mastheadStats(report: Report): StatItem[] {
  const coverage = report.coverage
  if (!coverage) return []
  const stats: StatItem[] = []

  if (coverage.date_from && coverage.date_to) {
    stats.push({
      label: 'Period covered',
      value: `${formatIsoDate(coverage.date_from)} – ${formatIsoDate(coverage.date_to)}`,
    })
  }
  if (coverage.transaction_count) {
    stats.push({
      label: 'Transactions reviewed',
      value: coverage.transaction_count.toLocaleString(),
    })
  }
  if (coverage.accounts?.length) {
    const accounts = coverage.accounts
    stats.push({
      label: accounts.length === 1 ? 'Account reviewed' : 'Accounts reviewed',
      value:
        accounts.length <= ACCOUNT_DETAIL_LIMIT
          ? accounts.map((last4) => `••${last4}`).join(', ')
          : accounts.length.toLocaleString(),
    })
  }
  return stats
}

function isRecommendationsSection(section: ReportSection): boolean {
  return (
    (section.id ?? '').trim().toLowerCase().replace(/-/g, '_') ===
    RECOMMENDATIONS_SECTION_ID
  )
}
